/**
 * Unify HealthcareService model.
 *
 * Represents a platform-catalog appointment-type template (session, meeting,
 * time-entry, episode-of-care). Catalog entries are global (no per-practice copy);
 * a provider is authorized for a type via `PractitionerRole.healthcareService`.
 * Used to describe the types of therapy and clinical services available
 * for intake matching, scheduling, and caseload filtering.
 *
 * ┌──────────────────────┬───────────────────────────────────────────────────────────┐
 * │ Field                │ Description                                               │
 * ├──────────────────────┼───────────────────────────────────────────────────────────┤
 * │ active               │ boolean — whether the service is currently offered        │
 * │ providedBy           │ Reference<Organization> — the practice offering the svc   │
 * │ location[]           │ Reference<Location>[] — where the service is delivered    │
 * │ name                 │ Human-readable service name                               │
 * │ type[]               │ CodeableConcept[] — service type codes                    │
 * │ specialty[]          │ CodeableConcept[] — clinical specialties                  │
 * │ category[]           │ CodeableConcept[] — broad service category                │
 * │ telecom[]            │ Contact details specific to this service                  │
 * │ appointmentRequired  │ boolean — whether booking requires an appointment         │
 * └──────────────────────┴───────────────────────────────────────────────────────────┘
 *
 * Extension summary (calendar event type catalog):
 *   SchedulingParameters.duration — default block length (minutes)
 *   charge-item-definition-reference — default billing definition (Reference)
 *   default-session-rate — legacy clinical session default rate (Money)
 *   note-template-id — default note template id (String)
 *   online-booking — whether clients can self-book (Boolean)
 *   intake-only — intake flow only, excluded from provider calendar (Boolean)
 *
 * Extension summary (episode-of-care type catalog):
 *   eoc-type-intake-type — intake behavior: both | consultations | intake-sessions (valueCode)
 *   eoc-type-session-map — nested refs to clinical-session HealthcareService ids (intake, consultation, standard)
 *   eoc-type-sohar-specialty-code — 3-letter uppercase Sohar specialty code (valueString)
 *   eoc-type-approved-sessions — repeated valueReference per additional approved clinical-session id (extras only; session-map entries are implicitly approved)
 *
 * Relationships:
 *   - HealthcareService.location[] → Location (site or room)
 *   - PractitionerRole.healthcareService[] → HealthcareService (per-provider authorization)
 */
import type { Extension, HealthcareService, Reference } from '@medplum/fhirtypes';
import {
  APPOINTMENT_NOTE_TEMPLATE_ID_REMAP,
  CALENDAR_EVENT_CATEGORY_SYSTEM,
  CHARGE_ITEM_DEFINITION_REF_EXT,
  DEFAULT_SESSION_RATE_EXT,
  EOC_TYPE_INTAKE_TYPE_EXT,
  EOC_TYPE_APPROVED_SESSIONS_EXT,
  EOC_TYPE_SESSION_MAP_EXT,
  EOC_TYPE_SOHAR_SPECIALTY_CODE_EXT,
  EXEMPLAR_NOTE_QR_SELECTION_EXT,
  HEALTHCARE_SERVICE_KEY_SYSTEM,
  INTAKE_ONLY_EXT,
  NOTE_TEMPLATE_ID_EXT,
  ONLINE_BOOKING_EXT,
  PLATFORM_HEALTHCARE_SERVICE_IDENTIFIER_SYSTEM,
  PLATFORM_HEALTHCARE_SERVICE_IDENTIFIER_VALUE,
  SCHEDULING_PARAMETERS_EXT,
  SESSION_MODALITY_SYSTEM,
  SESSION_TYPE_GROUP_SYSTEM,
} from '../constants';

// Only `parseHealthcareServiceId` is used in this file's body; the remaining entitlement
// helpers are re-exported (not imported) via the `export { … } from` block below.
import { parseHealthcareServiceId } from './healthcare-service-entitlement';

// ═══════════════════════════════════════════════════════════════════════
//  Getter helpers
// ═══════════════════════════════════════════════════════════════════════

/** Returns the stable platform catalog key from identifier, if present. */
export function getHealthcareServiceCatalogKey(hs: HealthcareService): string | undefined {
  return hs.identifier?.find((id) => id.system === HEALTHCARE_SERVICE_KEY_SYSTEM)?.value;
}

/** Returns the providing Organization reference. */
export function getHealthcareServiceOrgRef(svc: HealthcareService): Reference | undefined {
  return svc.providedBy;
}

/** Returns all Location references where the service is delivered. */
export function getHealthcareServiceLocationRefs(svc: HealthcareService): Reference[] {
  return svc.location ?? [];
}

/** Returns the service display name. */
export function getHealthcareServiceName(svc: HealthcareService): string | undefined {
  return svc.name;
}

/** Returns whether the service is active. */
export function isHealthcareServiceActive(svc: HealthcareService): boolean {
  return svc.active ?? true;
}

/** Returns the first type code string (e.g. therapy type). */
export function getHealthcareServiceTypeCode(svc: HealthcareService): string | undefined {
  return svc.type?.[0]?.coding?.[0]?.code;
}

export type SessionTypeGroup = 'screening' | 'psychotherapy' | 'psychology' | 'psychiatry';

/**
 * Read-only: clinical session group previously encoded in HealthcareService.type
 * (`SESSION_TYPE_GROUP_SYSTEM`). ENG-3443 stopped writing this coding from Settings → Services.
 */
export function getSessionTypeGroup(svc: HealthcareService): SessionTypeGroup | undefined {
  const code = svc.type?.flatMap((type) => type.coding ?? [])
    .find((coding) => coding.system === SESSION_TYPE_GROUP_SYSTEM)?.code;
  return code === 'screening'
    || code === 'psychotherapy'
    || code === 'psychology'
    || code === 'psychiatry'
    ? code
    : undefined;
}

/** Returns the default ChargeItemDefinition reference for this session type. */
export function getChargeItemDefinitionRef(svc: HealthcareService): Reference | undefined {
  return svc.extension?.find((ext) => ext.url === CHARGE_ITEM_DEFINITION_REF_EXT)?.valueReference;
}

/** Sets or clears the singular default ChargeItemDefinition reference. */
export function setChargeItemDefinitionRef(
  svc: HealthcareService,
  reference: Reference | undefined,
): HealthcareService {
  const extension = (svc.extension ?? []).filter((ext) => ext.url !== CHARGE_ITEM_DEFINITION_REF_EXT);
  if (reference?.reference) {
    extension.push({ url: CHARGE_ITEM_DEFINITION_REF_EXT, valueReference: reference });
  }
  return { ...svc, extension };
}

/** Returns all specialty code strings. */
export function getHealthcareServiceSpecialtyCodes(svc: HealthcareService): string[] {
  return (svc.specialty ?? []).flatMap(
    (s) => s.coding?.map((c) => c.code ?? '') ?? [],
  ).filter(Boolean);
}

/** Returns the calendar event category code (clinical-session, meeting, time-entry). */
export function getHealthcareServiceCategory(svc: HealthcareService): string | undefined {
  return svc.category?.[0]?.coding?.find((c) => c.system === CALENDAR_EVENT_CATEGORY_SYSTEM)?.code
    ?? svc.category?.[0]?.coding?.[0]?.code;
}

/** Returns default duration in minutes from SchedulingParameters extension. */
export function getSchedulingParametersDuration(svc: HealthcareService): number | undefined {
  const sched = svc.extension?.find((e) => e.url === SCHEDULING_PARAMETERS_EXT);
  const duration = sched?.extension?.find((e) => e.url === 'duration');
  const value = duration?.valueDuration?.value;
  return value !== undefined ? Math.round(value) : undefined;
}

/** Returns default session rate from default-session-rate extension. */
export function getDefaultSessionRate(svc: HealthcareService): number | undefined {
  const ext = svc.extension?.find((e) => e.url === DEFAULT_SESSION_RATE_EXT);
  return ext?.valueMoney?.value;
}

/** Returns note template id from note-template-id extension. */
export function getNoteTemplateId(svc: HealthcareService): string | null {
  const ext = svc.extension?.find((e) => e.url === NOTE_TEMPLATE_ID_EXT);
  return ext?.valueString ?? null;
}

export function setNoteTemplateId(svc: HealthcareService, templateId: string): HealthcareService {
  const others = (svc.extension ?? []).filter((e) => e.url !== NOTE_TEMPLATE_ID_EXT);
  return {
    ...svc,
    extension: [...others, { url: NOTE_TEMPLATE_ID_EXT, valueString: templateId }],
  };
}

/** Returns a copy with note-template-id remapped, or null when no change is needed. */
export function withRemappedNoteTemplateId(svc: HealthcareService): HealthcareService | null {
  const current = getNoteTemplateId(svc)?.trim();
  if (!current) return null;
  const next = APPOINTMENT_NOTE_TEMPLATE_ID_REMAP[current];
  if (!next || next === current) return null;
  return setNoteTemplateId(svc, next);
}

/** Returns session modality code (in-person, telehealth, hybrid, either). */
export function getSessionModalityCode(svc: HealthcareService): string | undefined {
  return svc.serviceProvisionCode?.[0]?.coding?.find((c) => c.system === SESSION_MODALITY_SYSTEM)?.code
    ?? svc.serviceProvisionCode?.[0]?.coding?.[0]?.code;
}

/** Returns online-booking flag; defaults to false when extension absent. */
export function getOnlineBooking(svc: HealthcareService): boolean {
  const ext = svc.extension?.find((e) => e.url === ONLINE_BOOKING_EXT);
  return ext?.valueBoolean ?? false;
}

/** Returns intake-only flag; defaults to false when extension absent. */
export function getIntakeOnly(svc: HealthcareService): boolean {
  const ext = svc.extension?.find((e) => e.url === INTAKE_ONLY_EXT);
  return ext?.valueBoolean ?? false;
}

/**
 * Returns the selected exemplar QuestionnaireResponse reference strings
 * (e.g. `QuestionnaireResponse/<id>`) for this service type.
 */
export function getExemplarNoteQrRefStrings(svc: HealthcareService): string[] {
  return (svc.extension ?? [])
    .filter((e) => e.url === EXEMPLAR_NOTE_QR_SELECTION_EXT && e.valueReference?.reference)
    .map((e) => e.valueReference!.reference!);
}

/**
 * Returns a new HealthcareService with the selected exemplar QR references set.
 * Replaces all existing exemplar QR selection extensions; preserves all others.
 * Each ref may be a full reference string (`QuestionnaireResponse/<id>`) or just an id;
 * bare ids are normalized to the full reference format.
 */
export function setExemplarNoteQrRefStrings(svc: HealthcareService, refs: string[]): HealthcareService {
  const others = (svc.extension ?? []).filter((e) => e.url !== EXEMPLAR_NOTE_QR_SELECTION_EXT);
  const newExts = refs
    .filter(Boolean)
    .map((ref) => ({
      url: EXEMPLAR_NOTE_QR_SELECTION_EXT,
      valueReference: {
        reference: ref.includes('/') ? ref : `QuestionnaireResponse/${ref}`,
      },
    }));
  return { ...svc, extension: [...others, ...newExts] };
}

/** Returns whether this resource is marked as a platform catalog entry. */
export function hasPlatformCatalogIdentifier(svc: HealthcareService): boolean {
  return (
    svc.identifier?.some(
      (id) =>
        id.system === PLATFORM_HEALTHCARE_SERVICE_IDENTIFIER_SYSTEM
        && id.value === PLATFORM_HEALTHCARE_SERVICE_IDENTIFIER_VALUE,
    ) ?? false
  );
}

export type EocIntakeType = 'both' | 'consultations' | 'intake-sessions';

export interface EocSessionMap {
  intake: string | null;
  consultation: string | null;
  standard: string | null;
}

/** Returns intake behavior for an episode-of-care type catalog entry. */
export function getEocTypeIntakeType(svc: HealthcareService): EocIntakeType {
  const ext = svc.extension?.find((e) => e.url === EOC_TYPE_INTAKE_TYPE_EXT);
  const code = ext?.valueCode;
  if (code === 'consultations' || code === 'intake-sessions') return code;
  return 'both';
}

/** Returns the Sohar specialty code for an episode-of-care type catalog entry, or null if unset. */
export function getEocTypeSoharSpecialtyCode(svc: HealthcareService): string | null {
  return svc.extension?.find((e) => e.url === EOC_TYPE_SOHAR_SPECIALTY_CODE_EXT)?.valueString ?? null;
}

/** Returns clinical-session HealthcareService ids mapped to intake/consultation/standard roles. */
export function getEocTypeSessionMap(svc: HealthcareService): EocSessionMap {
  const mapExt = svc.extension?.find((e) => e.url === EOC_TYPE_SESSION_MAP_EXT);
  const subExts = mapExt?.extension ?? [];
  const getRef = (url: string): string | null =>
    parseHealthcareServiceId(subExts.find((e) => e.url === url)?.valueReference) ?? null;
  return {
    intake: getRef('intake'),
    consultation: getRef('consultation'),
    standard: getRef('standard'),
  };
}

/**
 * Returns the additional approved clinical-session HealthcareService ids for an
 * episode-of-care type (one repeated extension entry per id). The
 * intake/consultation/standard session-map entries are implicitly approved and
 * are NOT duplicated here.
 */
export function getEocTypeApprovedSessionIds(svc: HealthcareService): string[] {
  return (svc.extension ?? [])
    .filter((e) => e.url === EOC_TYPE_APPROVED_SESSIONS_EXT)
    .map((e) => parseHealthcareServiceId(e.valueReference))
    .filter((id): id is string => Boolean(id));
}

/** Writes episode-of-care type extensions; returns the merged extension array. */
export function setEocTypeExtensions(
  svc: HealthcareService,
  intakeType: EocIntakeType,
  sessionMap: EocSessionMap,
  soharSpecialtyCode: string,
  approvedSessionIds: string[],
): Extension[] {
  const others = (svc.extension ?? []).filter(
    (e) =>
      e.url !== EOC_TYPE_INTAKE_TYPE_EXT
      && e.url !== EOC_TYPE_SESSION_MAP_EXT
      && e.url !== EOC_TYPE_SOHAR_SPECIALTY_CODE_EXT
      && e.url !== EOC_TYPE_APPROVED_SESSIONS_EXT,
  );
  const sessionSubExts: Extension[] = [];
  for (const key of ['intake', 'consultation', 'standard'] as const) {
    const id = sessionMap[key];
    if (id) {
      sessionSubExts.push({
        url: key,
        valueReference: { reference: `HealthcareService/${id}` },
      });
    }
  }
  others.push({ url: EOC_TYPE_INTAKE_TYPE_EXT, valueCode: intakeType });
  if (sessionSubExts.length > 0) {
    others.push({ url: EOC_TYPE_SESSION_MAP_EXT, extension: sessionSubExts });
  }
  if (soharSpecialtyCode) {
    others.push({ url: EOC_TYPE_SOHAR_SPECIALTY_CODE_EXT, valueString: soharSpecialtyCode });
  }
  for (const id of [...new Set(approvedSessionIds)]) {
    if (!id) continue;
    others.push({
      url: EOC_TYPE_APPROVED_SESSIONS_EXT,
      valueReference: { reference: `HealthcareService/${id}` },
    });
  }
  return others;
}

/** Session-map keys required before a type can be activated for the given intake type. */
export function getRequiredEocSessionKeys(intakeType: EocIntakeType): (keyof EocSessionMap)[] {
  switch (intakeType) {
    case 'consultations':
      return ['consultation', 'standard'];
    case 'intake-sessions':
      return ['intake', 'standard'];
    default:
      return ['intake', 'consultation', 'standard'];
  }
}

/** Returns true when every session type applicable to the intake type is set. */
export function hasRequiredEocSessions(intakeType: EocIntakeType, sessionMap: EocSessionMap): boolean {
  return getRequiredEocSessionKeys(intakeType).every((k) => Boolean(sessionMap[k]));
}

export {
  buildPracticeEnabledServicesList,
  getEnabledHealthcareServiceIdsFromList,
  getProviderHealthcareServiceIds,
  healthcareServiceReference,
  isPlatformCatalogHealthcareService,
  isPracticeEnabledServicesList,
  markHealthcareServiceAsPlatformCatalog,
  parseHealthcareServiceId,
  setProviderHealthcareServiceIds,
} from './healthcare-service-entitlement';
